import React, { useState, useCallback, useMemo } from 'react';
import { VALORES_REFERENCIA, calcularLAeq, calcularMaximo, calcularMinimo } from '../utils/calculosSonoros';

/**
 * Parseia texto colado para extrair valores de dB
 * Aceita valores separados por vírgula, ponto e vírgula, tab, quebra de linha ou espaço
 * @param {string} text - Texto colado pelo usuário
 * @returns {number[]} - Array de valores numéricos
 */
const parseValoresColados = (text) => {
  if (!text || !text.trim()) return [];
  
  // Divide por múltiplos separadores possíveis
  const partes = text
    .replace(/[,;]/g, ' ')  // Substitui vírgula e ponto-vírgula por espaço
    .replace(/\t/g, ' ')     // Substitui tab por espaço
    .replace(/\n/g, ' ')     // Substitui quebra de linha por espaço
    .split(/\s+/)            // Divide por espaços
    .filter(p => p.trim() !== '');
  
  const valores = [];
  for (const parte of partes) {
    // Substitui vírgula decimal por ponto
    const valorStr = parte.replace(',', '.');
    const valor = parseFloat(valorStr);
    if (!isNaN(valor) && valor >= 0 && valor <= 200) {
      valores.push(valor);
    }
  }
  
  return valores;
};

/**
 * Componente de Formulário para Pontos de Medição
 */
const FormularioPonto = ({ onAddPonto, medicaoData, onClearMedicao }) => {
  // Modo de entrada de dados: 'microfone' ou 'colar'
  const [modoEntrada, setModoEntrada] = useState('microfone');
  
  // Dados colados pelo usuário
  const [dadosColados, setDadosColados] = useState('');
  const [dadosTonalColados, setDadosTonalColados] = useState('');
  
  const [formData, setFormData] = useState({
    legenda: '',
    tipo: 'especifico',
    valorReferencia: 55,
    legislacao: 'NBR 10151 - Área mista predominantemente residencial (diurno)',
    equipamento: 'Dispositivo móvel com microfone calibrado',
    coordenadaX: '',
    coordenadaY: '',
    periodo: 'diurno',
    zonaUso: 'mistaPredominantementeResidencial'
  });

  /**
   * Atualiza campo do formulário
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Atualiza valor de referência automaticamente baseado em zona e período
      if (name === 'periodo' || name === 'zonaUso') {
        const periodo = name === 'periodo' ? value : prev.periodo;
        const zona = name === 'zonaUso' ? value : prev.zonaUso;
        updated.valorReferencia = VALORES_REFERENCIA[periodo]?.[zona] || 55;
      }
      
      return updated;
    });
  }, []);

  /**
   * Calcula estatísticas dos dados colados
   */
  const dadosColadosStats = useMemo(() => {
    if (modoEntrada !== 'colar' || !dadosColados.trim()) return null;
    
    const valores = parseValoresColados(dadosColados);
    if (valores.length === 0) return null;
    
    return {
      valores,
      laeq: calcularLAeq(valores),
      max: calcularMaximo(valores),
      min: calcularMinimo(valores),
      count: valores.length
    };
  }, [modoEntrada, dadosColados]);

  /**
   * Verifica se tem dados válidos para submissão
   */
  const temMedicaoValida = useMemo(() => {
    if (modoEntrada === 'microfone') {
      return medicaoData && medicaoData.valores && medicaoData.valores.length > 0;
    } else {
      return dadosColadosStats && dadosColadosStats.valores.length > 0;
    }
  }, [modoEntrada, medicaoData, dadosColadosStats]);

  /**
   * Submete o formulário
   */
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    let valores, stats, frequencyDataFinal, samplesFinal;
    
    if (modoEntrada === 'microfone') {
      if (!medicaoData || medicaoData.valores.length === 0) {
        alert('Realize uma medição antes de adicionar o ponto.');
        return;
      }
      valores = medicaoData.valores;
      stats = medicaoData.stats;
      frequencyDataFinal = medicaoData.frequencyData || [];
      samplesFinal = medicaoData.samples || [];
    } else {
      // Modo colar dados
      if (!dadosColadosStats || dadosColadosStats.valores.length === 0) {
        alert('Cole os valores de medição (dB) no campo correspondente.');
        return;
      }
      valores = dadosColadosStats.valores;
      stats = {
        laeq: dadosColadosStats.laeq,
        max: dadosColadosStats.max,
        min: dadosColadosStats.min,
        count: dadosColadosStats.count
      };
      
      // Processa dados tonais se fornecidos
      const valoresTonal = parseValoresColados(dadosTonalColados);
      frequencyDataFinal = valoresTonal.length > 0 
        ? valoresTonal.map((db, idx) => ({ freq: idx * 100, db }))
        : [];
      samplesFinal = valores.map((db, idx) => ({ timestamp: Date.now() + idx * 5000, db }));
    }
    
    const novoPonto = {
      ...formData,
      valores,
      frequencyData: frequencyDataFinal,
      samples: samplesFinal,
      stats,
      timestamp: Date.now(),
      origem: modoEntrada === 'colar' ? 'dados_externos' : 'microfone'
    };
    
    onAddPonto(novoPonto);
    
    // Limpa dados da medição após salvar
    if (modoEntrada === 'microfone' && onClearMedicao) {
      onClearMedicao();
    } else {
      setDadosColados('');
      setDadosTonalColados('');
    }
    
    // Reseta formulário mas mantém configurações de referência
    setFormData(prev => ({
      ...prev,
      legenda: '',
      coordenadaX: '',
      coordenadaY: ''
    }));
  }, [formData, medicaoData, modoEntrada, dadosColadosStats, dadosTonalColados, onAddPonto, onClearMedicao]);

  /**
   * Obtém localização GPS
   */
  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocalização não suportada neste dispositivo.');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Coordenadas em decimal (podem ser convertidas para UTM se necessário)
        setFormData(prev => ({
          ...prev,
          coordenadaX: position.coords.longitude.toFixed(6),
          coordenadaY: position.coords.latitude.toFixed(6)
        }));
      },
      (error) => {
        alert('Erro ao obter localização: ' + error.message);
      },
      { enableHighAccuracy: true }
    );
  }, []);

  const temMedicao = medicaoData && medicaoData.valores && medicaoData.valores.length > 0;

  return (
    <div className="formulario-container">
      <div className="formulario-header">
        <h3>📝 Dados do Ponto de Medição</h3>
      </div>

      {/* Seletor de modo de entrada */}
      <div className="modo-entrada-selector">
        <label className="modo-entrada-label">Origem dos dados:</label>
        <div className="modo-entrada-options">
          <button
            type="button"
            className={`modo-btn ${modoEntrada === 'microfone' ? 'active' : ''}`}
            onClick={() => setModoEntrada('microfone')}
          >
            🎤 Capturar pelo Microfone
          </button>
          <button
            type="button"
            className={`modo-btn ${modoEntrada === 'colar' ? 'active' : ''}`}
            onClick={() => setModoEntrada('colar')}
          >
            📋 Colar Dados Externos
          </button>
        </div>
      </div>

      {/* Seção de dados colados (quando modo = colar) */}
      {modoEntrada === 'colar' && (
        <div className="dados-colados-section">
          <div className="form-group full-width">
            <label htmlFor="dadosColados">
              Nível de pressão sonora contínuo equivalente ponderado em A (LAeq) - Cole os valores em dB *
            </label>
            <textarea
              id="dadosColados"
              value={dadosColados}
              onChange={(e) => setDadosColados(e.target.value)}
              rows={4}
              placeholder="Cole aqui os valores de medição. Ex: 45.2, 48.1, 47.5, 46.8...
Aceita valores separados por vírgula, ponto e vírgula, tab ou quebra de linha."
              className="textarea-dados"
            />
            <small>
              {dadosColadosStats 
                ? `${dadosColadosStats.count} valores detectados` 
                : 'Nenhum valor detectado'}
            </small>
          </div>

          <div className="form-group full-width">
            <label htmlFor="dadosTonalColados">
              Níveis por banda de frequência (Som Tonal) - Opcional
            </label>
            <textarea
              id="dadosTonalColados"
              value={dadosTonalColados}
              onChange={(e) => setDadosTonalColados(e.target.value)}
              rows={3}
              placeholder="Cole aqui os valores de 1/3 de oitava, se disponíveis (opcional)"
              className="textarea-dados"
            />
          </div>

          {/* Resumo dos dados colados */}
          {dadosColadosStats && (
            <div className="medicao-resumo dados-colados">
              <h4>📊 Resumo dos Dados Colados</h4>
              <div className="resumo-grid">
                <div className="resumo-item">
                  <span className="resumo-label">LAeq:</span>
                  <span className="resumo-valor">{dadosColadosStats.laeq.toFixed(1)} dB</span>
                </div>
                <div className="resumo-item">
                  <span className="resumo-label">Máximo:</span>
                  <span className="resumo-valor">{dadosColadosStats.max.toFixed(1)} dB</span>
                </div>
                <div className="resumo-item">
                  <span className="resumo-label">Mínimo:</span>
                  <span className="resumo-valor">{dadosColadosStats.min.toFixed(1)} dB</span>
                </div>
                <div className="resumo-item">
                  <span className="resumo-label">Amostras:</span>
                  <span className="resumo-valor">{dadosColadosStats.count}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Resumo da medição atual (modo microfone) */}
      {modoEntrada === 'microfone' && temMedicao && (
        <div className="medicao-resumo">
          <h4>📊 Medição Capturada</h4>
          <div className="resumo-grid">
            <div className="resumo-item">
              <span className="resumo-label">LAeq:</span>
              <span className="resumo-valor">{medicaoData.stats.laeq.toFixed(1)} dB</span>
            </div>
            <div className="resumo-item">
              <span className="resumo-label">Máximo:</span>
              <span className="resumo-valor">{medicaoData.stats.max.toFixed(1)} dB</span>
            </div>
            <div className="resumo-item">
              <span className="resumo-label">Mínimo:</span>
              <span className="resumo-valor">{medicaoData.stats.min.toFixed(1)} dB</span>
            </div>
            <div className="resumo-item">
              <span className="resumo-label">Amostras:</span>
              <span className="resumo-valor">{medicaoData.stats.count}</span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="legenda">Legenda/Descrição do Ponto *</label>
            <input
              type="text"
              id="legenda"
              name="legenda"
              value={formData.legenda}
              onChange={handleChange}
              placeholder="Ex: Ponto 1 - Divisa leste do terreno"
              required
              maxLength={100}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="tipo">Tipo do Ponto *</label>
            <select
              id="tipo"
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              required
            >
              <option value="residual">Residual (referência sem a fonte)</option>
              <option value="especifico">Específico (com a fonte investigada)</option>
            </select>
            <small>
              {formData.tipo === 'residual' 
                ? 'O ponto residual é medido sem a interferência da fonte sonora investigada'
                : 'O ponto específico é medido com a fonte sonora em operação'}
            </small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="periodo">Período *</label>
            <select
              id="periodo"
              name="periodo"
              value={formData.periodo}
              onChange={handleChange}
              required
            >
              <option value="diurno">Diurno (7h às 19h)</option>
              <option value="noturno">Noturno (19h às 7h)</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="zonaUso">Zona de Uso *</label>
            <select
              id="zonaUso"
              name="zonaUso"
              value={formData.zonaUso}
              onChange={handleChange}
              required
            >
              <option value="residencialRural">Residencial rural</option>
              <option value="residencialUrbana">Estritamente residencial urbana / hospitais / escolas</option>
              <option value="mistaPredominantementeResidencial">Mista predominantemente residencial</option>
              <option value="mistaComercial">Mista com atividades comerciais/administrativas</option>
              <option value="mistaCulturalLazer">Mista com atividades culturais/lazer/turismo</option>
              <option value="industrial">Predominantemente industrial</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="valorReferencia">Valor de Referência (dB)</label>
            <input
              type="number"
              id="valorReferencia"
              name="valorReferencia"
              value={formData.valorReferencia}
              onChange={handleChange}
              min={30}
              max={90}
              required
            />
            <small>Definido automaticamente conforme NBR 10151</small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="legislacao">Legislação Aplicável</label>
            <input
              type="text"
              id="legislacao"
              name="legislacao"
              value={formData.legislacao}
              onChange={handleChange}
              placeholder="Ex: NBR 10151, Lei Municipal nº..."
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="equipamento">Equipamento Utilizado</label>
            <textarea
              id="equipamento"
              name="equipamento"
              value={formData.equipamento}
              onChange={handleChange}
              rows={2}
              placeholder="Descrição do equipamento de medição"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="coordenadaX">Longitude (X)</label>
            <input
              type="text"
              id="coordenadaX"
              name="coordenadaX"
              value={formData.coordenadaX}
              onChange={handleChange}
              placeholder="Ex: -49.123456"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="coordenadaY">Latitude (Y)</label>
            <input
              type="text"
              id="coordenadaY"
              name="coordenadaY"
              value={formData.coordenadaY}
              onChange={handleChange}
              placeholder="Ex: -27.654321"
            />
          </div>
          
          <div className="form-group location-btn">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={getLocation}
            >
              📍 Obter Localização
            </button>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-success"
            disabled={!temMedicaoValida}
          >
            ✓ Adicionar Ponto
          </button>
          
          {!temMedicaoValida && (
            <span className="form-hint">
              {modoEntrada === 'microfone' 
                ? '⚠️ Realize uma medição primeiro usando o medidor acima'
                : '⚠️ Cole os valores de medição no campo acima'}
            </span>
          )}
        </div>
      </form>

      {/* Tabela de referência */}
      <div className="referencia-info">
        <h4>📋 Valores de Referência NBR 10151</h4>
        <table className="tabela-referencia">
          <thead>
            <tr>
              <th>Zona de Uso</th>
              <th>Diurno</th>
              <th>Noturno</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Residencial rural</td>
              <td>40 dB</td>
              <td>35 dB</td>
            </tr>
            <tr>
              <td>Estritamente residencial urbana</td>
              <td>50 dB</td>
              <td>45 dB</td>
            </tr>
            <tr>
              <td>Mista predominantemente residencial</td>
              <td>55 dB</td>
              <td>50 dB</td>
            </tr>
            <tr>
              <td>Mista comercial/administrativa</td>
              <td>60 dB</td>
              <td>55 dB</td>
            </tr>
            <tr>
              <td>Mista cultural/lazer/turismo</td>
              <td>65 dB</td>
              <td>55 dB</td>
            </tr>
            <tr>
              <td>Predominantemente industrial</td>
              <td>70 dB</td>
              <td>60 dB</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FormularioPonto;
